import { useEffect, useMemo } from 'react';
import { useFetcher } from '@remix-run/react';
import { useAtomValue } from 'jotai';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import {
    QUERY_DIRECTORY_ITEMS_ADMIN,
    type loader,
} from '~/routes/remix-api.directory.get-directory-items-admin';
import { useRootPermissions } from '~/modules/user';
import type { DirectoryItemAdmin } from '../type';

/**
 * Hook to get the admin metadata for directory items, if the current user has
 * the appropriate permissions.
 * @param itemIDs The IDs of the items to get the admin data for.
 * @returns A map of item IDs to their admin metadata.
 */
export function useItemAdminData(itemIDs: number[]) {
    const fetcher = useFetcher<typeof loader>();
    const { editProfile } = useRootPermissions();
    const { isDesktop } = useAtomValue(uiContextAtomDisplayMode);
    const ids = [...itemIDs].sort();

    useEffect(() => {
        if (!isDesktop || !editProfile || !ids.length) {
            return;
        }

        const formData = new FormData();
        ids.forEach((id) => formData.append('IDs', id.toString()));
        fetcher.submit(formData, {
            method: 'get',
            action: QUERY_DIRECTORY_ITEMS_ADMIN,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDesktop, editProfile, JSON.stringify(ids)]);

    return useMemo(() => {
        if (!editProfile) {
            return {};
        }
        const adminData = fetcher.data?.items || [];
        return adminData.reduce(
            (acc, item) => {
                acc[item.id] = item;
                return acc;
            },
            {} as { [key: number]: DirectoryItemAdmin }
        );
    }, [fetcher.data, editProfile]);
}
